import type { ElementType } from "react"
import { useState, useEffect, useMemo } from "react"
import {
  Button,
  HStack,
  VStack,
  Text,
  Box,
  Icon,
} from "@chakra-ui/react"
import { Checkbox } from "@hydrologiq/shared/chakra/Checkbox"
import { FaFilter, FaCheck, FaBolt, FaPlug, FaFlask, FaGasPump, FaBatteryFull } from "react-icons/fa6"
import Dialog from "@hydrologiq/shared/chakra/Dialog"
import type { FieldGroup } from "./fieldGroups"

/** Period field keys that are always included regardless of user selection */
const PERIOD_FIELD_KEYS = ["bucket_start", "bucket_end"]

export interface GroupedFieldSelectorProps {
  /** Canonical field groups with color/icon/fields */
  groups: FieldGroup[]
  /** Fields available for the current system (from API meta.available_fields) */
  availableFields: string[]
  /** Currently selected field keys */
  selectedFields: string[]
  /** Callback when field selection changes */
  onFieldsChange: (fields: string[]) => void
}

/** Map group icon names to react-icons components */
const ICON_MAP: Record<string, ElementType> = {
  bolt: FaBolt,
  plug: FaPlug,
  flask: FaFlask,
  "gas-pump": FaGasPump,
  battery: FaBatteryFull,
}

/**
 * GroupedFieldSelector displays fields organized into role-based groups
 * with color/icon matching the platform palette.
 *
 * - Groups with no available fields are hidden
 * - Period fields (bucket_start, bucket_end) are always included
 * - Per-group "select all / deselect all" toggle
 */
export function GroupedFieldSelector({
  groups,
  availableFields,
  selectedFields,
  onFieldsChange,
}: GroupedFieldSelectorProps) {
  const [open, setOpen] = useState(false)
  const [tempSelection, setTempSelection] = useState<string[]>(selectedFields)

  // Sync temp selection with prop changes
  useEffect(() => {
    setTempSelection(selectedFields)
  }, [selectedFields])

  /** Visible groups: only those with at least one field in availableFields (excluding period) */
  const visibleGroups = useMemo(() => {
    return groups.filter((group) => {
      // Period group is always visible if it has fields
      if (group.key === "period") return false // Period is handled separately, not shown as selectable
      // Only show groups that have at least one available field
      return group.fields.some((field) => availableFields.includes(field.key))
    })
  }, [groups, availableFields])

  /** Get available fields within a group */
  const getGroupAvailableFields = (group: FieldGroup): string[] => {
    return group.fields
      .filter((field) => availableFields.includes(field.key))
      .map((field) => field.key)
  }

  /** Check if all available fields in a group are selected */
  const isGroupAllSelected = (group: FieldGroup): boolean => {
    const groupAvailable = getGroupAvailableFields(group)
    return groupAvailable.length > 0 && groupAvailable.every((key) => tempSelection.includes(key))
  }

  /** Select all available fields in a group */
  const handleSelectAllInGroup = (group: FieldGroup) => {
    const groupAvailable = getGroupAvailableFields(group)
    setTempSelection((prev) => {
      const withoutGroup = prev.filter((f) => !groupAvailable.includes(f))
      return [...withoutGroup, ...groupAvailable]
    })
  }

  /** Deselect all fields in a group */
  const handleDeselectAllInGroup = (group: FieldGroup) => {
    const groupAvailable = getGroupAvailableFields(group)
    setTempSelection((prev) => prev.filter((f) => !groupAvailable.includes(f)))
  }

  /** Toggle all fields in a group (select all if not all selected, deselect all otherwise) */
  const handleToggleGroup = (group: FieldGroup) => {
    if (isGroupAllSelected(group)) {
      handleDeselectAllInGroup(group)
    } else {
      handleSelectAllInGroup(group)
    }
  }

  /** Apply selection — always ensures period fields are included */
  const handleApply = () => {
    const finalSelection = [...tempSelection]
    // Always include period fields
    for (const periodKey of PERIOD_FIELD_KEYS) {
      if (!finalSelection.includes(periodKey)) {
        finalSelection.unshift(periodKey)
      }
    }
    onFieldsChange(finalSelection)
    setOpen(false)
  }

  /** Cancel and revert to current selection */
  const handleCancel = () => {
    setTempSelection(selectedFields)
    setOpen(false)
  }

  /** Count of selected non-period fields */
  const selectableSelected = tempSelection.filter(
    (f) => !PERIOD_FIELD_KEYS.includes(f)
  ).length
  const totalSelectable = availableFields.filter(
    (f) => !PERIOD_FIELD_KEYS.includes(f)
  ).length

  return (
    <Dialog
      title="Select fields"
      open={open}
      setOpen={setOpen}
      size="sm"
      showCloseButton={false}
      trigger={
        <Button variant="hydrologiq_secondary" size="sm">
          <HStack gap={2}>
            <FaFilter />
            <Text>
              Fields ({selectableSelected}/{totalSelectable})
            </Text>
          </HStack>
        </Button>
      }
      footer={
        <HStack justifyContent="flex-end" gap={2} width="100%">
          <Button size="sm" variant="hydrologiq_secondary" onClick={handleCancel}>
            Cancel
          </Button>
          <Button size="sm" variant="hydrologiq_primary" onClick={handleApply}>
            <HStack gap={1}>
              <FaCheck />
              <Text>Apply</Text>
            </HStack>
          </Button>
        </HStack>
      }
    >
      {/* Grouped field list */}
      <VStack align="stretch" gap={4} width="100%">
        {visibleGroups.map((group) => {
          const IconComponent = ICON_MAP[group.icon]
          const allSelected = isGroupAllSelected(group)

          return (
            <Box key={group.key}>
              {/* Group header with toggle */}
              <HStack justifyContent="space-between" mb={2}>
                <HStack gap={2}>
                  {IconComponent && (
                    <Icon color={group.color}>
                      <IconComponent />
                    </Icon>
                  )}
                  <Text fontSize="sm" fontWeight="semibold" color={group.color}>
                    {group.label}
                  </Text>
                </HStack>
                <Button
                  size="xs"
                  variant="hydrologiq_secondary"
                  onClick={() => handleToggleGroup(group)}
                >
                  {allSelected ? "Deselect all" : "Select all"}
                </Button>
              </HStack>

              {/* Fields in group */}
              <VStack align="stretch" gap={1} pl={6}>
                {group.fields
                  .filter((field) => availableFields.includes(field.key))
                  .map((field) => {
                    const isSelected = tempSelection.includes(field.key)
                    return (
                      <Checkbox
                        key={field.key}
                        checked={isSelected}
                        onCheckedChange={(e) => {
                          const checked = typeof e === "object" ? e.checked : e
                          if (checked) {
                            setTempSelection((prev) => [...prev, field.key])
                          } else {
                            setTempSelection((prev) => prev.filter((f) => f !== field.key))
                          }
                        }}
                      >
                        <Text fontSize="sm">{field.label}</Text>
                      </Checkbox>
                    )
                  })}
              </VStack>
            </Box>
          )
        })}
      </VStack>
    </Dialog>
  )
}

export default GroupedFieldSelector
